import React, { useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { faEdit } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";

import RenameNoteModal from "~/stream/RenameNoteModal";
import { useModals } from "~/ui/modals";

import {
	MenuItemToBeDisabledForExternalWorkspaces,
	MenuItemTooltip,
	useGetPropsForExternalWorkspaces,
} from "./MenuItemTooltip";

import type { AddNoteTitle_note$key } from "~/__generated__/AddNoteTitle_note.graphql";

const noteFragment = graphql`
	fragment AddNoteTitle_note on Note {
		id
		isLocked
		noteTitle: title

		stream {
			displayName
		}
		...MenuItemTooltip_useGetPropsForExternalWorkspaces_note
	}
`;

export default function AddNoteTitleMenuItem({ note: noteKey }: { note: AddNoteTitle_note$key }) {
	const note = useFragment(noteFragment, noteKey);

	const modals = useModals();
	const noteHasTitle = useMemo(() => Boolean(note.noteTitle && note.stream.displayName !== note.noteTitle), [note]);

	const openRenameModal = useCallback(
		() => modals.open(({ close }) => <RenameNoteModal id={note.id} initialTitle={note.noteTitle} onClose={close} />),
		[modals, note.id, note.noteTitle],
	);
	const tooltipPropsForExternalWorkspaces = useGetPropsForExternalWorkspaces(note);

	return (
		<MenuItemToBeDisabledForExternalWorkspaces
			icon={faEdit}
			onSelect={openRenameModal}
			disabled={note.isLocked}
			{...tooltipPropsForExternalWorkspaces}
		>
			<MenuItemTooltip
				disabled={!note.isLocked}
				content="Locked notes cannot be renamed."
				data-test-id="note-header-dropdown-renamenote-item"
			>
				{noteHasTitle ? <span>Edit note title…</span> : <span>Add note title…</span>}
			</MenuItemTooltip>
		</MenuItemToBeDisabledForExternalWorkspaces>
	);
}
